import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './gallery.css';
import { galleryData } from './galleryData.js'; // Import your data

const EventGallery = () => {
  // Instead of true/false, we store the entire data object of the clicked card
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (selectedItem) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [selectedItem]);

  return (
    <>
      <section className="event-gallery">
        <header className="event-gallery__header">
          <h1 className="event-gallery__title">
            <span className="event-gallery__title-bold"><span className="kireeti">TEDx</span>IITGuwahati</span> in Pics
          </h1>
        </header>

        <div className="event-gallery__grid">
          {galleryData.map((item) => (
            <article 
              key={item.id} 
              className="event-gallery__card"
              onClick={() => setSelectedItem(item)} // Set the clicked item as active
              style={{ cursor: 'pointer' }}
            >
              <img
                src={item.coverImage}
                alt={item.label}
                className="event-gallery__image"
                loading="lazy"
              />
              <div className="event-gallery__overlay">
                <span className="event-gallery__credit">{item.credit}</span>
                <h2 className="event-gallery__credit">{item.label}</h2>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Popup Overlay */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="popup-overlay"
          >
            <button 
              className="popup-close-btn"
              onClick={() => setSelectedItem(null)}
            >
              Close &times;
            </button>
            
            <div className="popup-scroll-content">
                {/* We pass the specific images for this category 
                   to the DragCards component 
                */}
                <DragCards images={selectedItem.dragImages} />
                
                {/* We pass the specific carousel images 
                   to the Carousel component 
                */}
                <Carousel items={selectedItem.carouselImages} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// --- Carousel Component (Unchanged logic, just receives dynamic props) ---
const Carousel = ({ items }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % items.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    };

    const currentItem = items[currentIndex];

    return (
        <section className="carousel-fullscreen">
            <div 
                className="carousel-backdrop"
                style={{ backgroundImage: `url(${currentItem.image})` }}
            ></div>
            <div className="carousel-backdrop-overlay"></div>

            <div className="carousel-content-wrapper">
                <button onClick={prevSlide} className="carousel-nav-btn prev-btn">&#10094;</button>

                <div className="carousel-image-container">
                    <motion.img 
                        key={currentIndex} 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        src={currentItem.image} 
                        alt={currentItem.label} 
                        className="carousel-main-image" 
                    />
                    <div className="carousel-image-caption">
                        <h3>{currentItem.label}</h3>
                        <p>{currentItem.credit}</p>
                    </div>
                </div>

                <button onClick={nextSlide} className="carousel-nav-btn next-btn">&#10095;</button>
            </div>
        </section>
    );
};

// --- Drag Cards Component (Updated to be Dynamic) ---

const DragCards = ({ images }) => {
  return (
    <section className="drag-section">
      <h2 className="drag-bg-text">
        <span className="drag-bg-text-highlight">TEDx</span>IITGuwahati
      </h2>
      <Cards images={images} />
      <div className="scroll-hint">
        Scroll Down &darr;
      </div>
    </section>
  );
};

const Cards = ({ images }) => {
  const containerRef = useRef(null);

  // Define predefined positions/rotations for the layout
  // We will map the incoming images to these slots.
  const slots = [
    { top: "20%", left: "25%", rotate: "6deg", className: "card-medium" },
    { top: "45%", left: "60%", rotate: "12deg", className: "card-small" },
    { top: "20%", left: "40%", rotate: "-6deg", className: "card-large" },
    { top: "50%", left: "40%", rotate: "8deg", className: "card-medium" },
    { top: "20%", left: "65%", rotate: "18deg", className: "card-medium" },
    { top: "35%", left: "55%", rotate: "-3deg", className: "card-small" },
  ];

  return (
    <div className="drag-cards-container" ref={containerRef}>
      {images.map((imgSrc, index) => {
        // If we have more images than slots, loop over slots
        const slot = slots[index % slots.length];
        
        return (
          <Card
            key={index}
            containerRef={containerRef}
            src={imgSrc}
            alt="Event highlight"
            top={slot.top}
            left={slot.left}
            rotate={slot.rotate}
            className={slot.className}
          />
        );
      })}
    </div>
  );
};

const Card = ({ containerRef, src, alt, top, left, rotate, className }) => {
  const [zIndex, setZIndex] = useState(0);

  const updateZIndex = () => {
    const els = document.querySelectorAll('.drag-card-element');
    let maxZIndex = -Infinity;
    els.forEach((el) => {
      let zIndex = parseInt(window.getComputedStyle(el).getPropertyValue('z-index'));
      if (!isNaN(zIndex) && zIndex > maxZIndex) maxZIndex = zIndex;
    });
    setZIndex(maxZIndex + 1);
  };

  return (
    <motion.img
      onMouseDown={updateZIndex}
      style={{ top, left, rotate, zIndex }}
      className={`drag-card-element ${className}`}
      src={src}
      alt={alt}
      drag
      dragConstraints={containerRef}
      dragElastic={0.65}
    />
  );
};

export default EventGallery;